import {
  Box,
  Button,
  DateRangePicker,
  Flex,
  Icon,
  InputGroup,
  Pane,
  Popover,
  Text,
} from '@fullstory/components';
import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isValidDate } from '@fullstory/util/date';
import { useDatePickerRetentionProps } from '../../hooks/useDatePickerRetentionProps';
import { DateRange, DateRanges } from '../../types';
import { dateRangeToString } from '../../utils';
import moment from 'moment';

export type ButtonProps = Pick<
  ComponentProps<typeof Button>,
  'disabled' | 'size' | 'variant' | 'iconLeft' | 'fullWidth' | 'testId' | 'id'
>;

export interface DateTimeRangePickerProps {
  value?: DateRange;
  onChange: (value: DateRange | undefined) => void;
  placeholder?: string;
  maxNumDays?: number;
  /**
   * If `true`, a button will show up next to the primary range dropdown that will clear the all values.
   * Defaults to `false`.
   */
  clearable?: boolean;
  buttonProps?: ButtonProps;
  popoverPlacement?: ComponentProps<typeof Popover>['placement'];
  utc?: ComponentProps<typeof DateRangePicker>['utc'];
  minDate?: ComponentProps<typeof DateRangePicker>['minDate'];
  maxDate?: ComponentProps<typeof DateRangePicker>['maxDate'];
  retentionTimestamp?: number;
  initialOpen?: boolean;
}

export const DateTimeRangePicker = ({
  value: injectedValue,
  onChange,
  placeholder,
  maxNumDays,
  clearable = false,
  buttonProps,
  popoverPlacement = 'bottom-end',
  utc,
  minDate,
  maxDate,
  retentionTimestamp,
  initialOpen = false,
}: DateTimeRangePickerProps) => {
  // NB: useState to force re-render when the anchor changes so that the popover is laid correctly if initially open
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(initialOpen);
  const [value, setValue] = useState(injectedValue);
  const [valid, setValid] = useState(true);
  const openPopover = () => setOpen(true);
  const closePopover = () => setOpen(false);

  const applyDisabled = !value || !isValidDate(value.start) || !isValidDate(value.end) || !valid;

  const onApplyClick = () => {
    onChange(value);
    closePopover();
  };

  const ranges: DateRanges = useMemo(
    () => [value ?? { start: null, end: null }, undefined],
    [value],
  );

  useEffect(() => {
    if (value) {
      setValid(isRangeValid(value, maxNumDays));
    }
  }, [value, maxNumDays]);

  const onClick = () => {
    if (open) {
      onCloseSignal();
    } else {
      openPopover();
    }
  };

  const onCancel = () => {
    setValue(injectedValue);
    closePopover();
  };

  const onClear = () => {
    setValue({ start: null, end: null });
    onChange(undefined);
    closePopover();
  };

  const onCloseSignal = () => {
    if (!applyDisabled) {
      onApplyClick();
    } else {
      onCancel();
    }
  };

  const {
    isDayDisabled: isRetentionDayDisabled,
    renderInfoTooltip,
    minDate: minRetentionDate,
  } = useDatePickerRetentionProps(retentionTimestamp);
  const isDayDisabled = useCallback(
    (day: Date) => {
      if (!maxNumDays) return isRetentionDayDisabled(day);
      const diffDays = moment().diff(moment(day), 'days');
      return isRetentionDayDisabled(day) || diffDays > maxNumDays;
    },
    [isRetentionDayDisabled, maxNumDays],
  );

  const placeholderText = placeholder ?? (
    <FormattedMessage defaultMessage="Select date range" id="qp/RxH" />
  );

  return (
    <Flex data-component="DateTimeRangePicker" ref={setAnchor} justifyContent="flex-start">
      <InputGroup>
        {clearable && injectedValue && (
          <Button
            onClick={onClear}
            size={buttonProps?.size}
            variant={buttonProps?.variant}
            testId="multi-column-time-picker-primary-clear-button"
          >
            <Icon color="inherit" name="close" size={16} />
          </Button>
        )}
        <Button
          variant="fill-builder"
          iconRight="chevron-down"
          onClick={onClick}
          {...buttonProps}
          testId={buttonProps?.testId ?? 'date-time-range-picker'}
        >
          {!injectedValue ? placeholderText : dateRangeToString(injectedValue)}
        </Button>
      </InputGroup>
      <Popover
        anchor={anchor}
        open={open}
        onCloseSignal={onCloseSignal}
        placement={popoverPlacement}
        offset={8}
        flipY={false}
        portal={false}
        positioningStrategy="fixed"
        variant="bordered"
      >
        <Pane width="min-content" overflow="hidden">
          <Flex column>
            <Box height={480}>
              <DateRangePicker
                ranges={ranges}
                onChange={ranges => setValue(ranges[0])}
                isDayDisabled={isDayDisabled}
                renderInfoTooltip={renderInfoTooltip}
                utc={utc}
                minDate={minRetentionDate ?? minDate}
                maxDate={maxDate}
              />
            </Box>
            <Pane padding={8} width="100%" borderTop="standard">
              <Flex justifyContent={!valid ? 'space-between' : 'flex-end'}>
                {!valid && (
                  <Flex justifyContent="flex-start">
                    <Box marginRight={16}>
                      <Icon name="exclamation" color="warning" size={16} />
                    </Box>
                    <Text appearance="bodySmall">{`Selected time range(s) must be ${maxNumDays} days or less.`}</Text>
                  </Flex>
                )}
                <Flex justifyContent="flex-end">
                  <Box marginRight={16}>
                    <Button onClick={onCancel} variant="minimal">
                      <FormattedMessage
                        defaultMessage="Cancel"
                        id="TblT2P"
                        description="Cancel date/time selection and close the popup"
                      />
                    </Button>
                  </Box>
                  <Button variant="fill-primary" onClick={onApplyClick} disabled={applyDisabled}>
                    <FormattedMessage
                      defaultMessage="Apply"
                      id="HMl7ew"
                      description="Apply the date/time selection and close the popup"
                    />
                  </Button>
                </Flex>
              </Flex>
            </Pane>
          </Flex>
        </Pane>
      </Popover>
    </Flex>
  );
};

function isRangeValid(range: DateRange, maxNumDays?: number): boolean {
  if (!maxNumDays || !range.end) return true;

  const startDate = moment(range.start ?? undefined);
  const endDate = moment(range.end ?? undefined);

  const diff = endDate.diff(startDate, 'days');
  return diff <= maxNumDays;
}
